import { createComponent, FelaComponent } from "react-fela";
import footerMenu from "~/data/footer-menu.json";
import footerData from "~/data/footer-data.json";
import { colors, gutters, layout } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import get from "lodash.get";

import WordpressLink from "~/components/global/links";
import Visual from "~/components/shared/visual";
import { ContentMaxWidth } from "~/components/layout/max-width";

export default function FooterNavigation() {
    const { data: footerMenuData } = footerMenu;
    const { data: footerDataData } = footerData;

    // Get the logo from WordPress or backup to a local SVG
    let ringLogo = get(footerDataData, "acf.footer_logo");
    if (!ringLogo)
        ringLogo = "/brand/ringDNA-logo-mark-white.svg";

    const footerSecurityLogos = footerDataData.acf.footer_security_logos;

    const renderSecurityImage = (logo, index) => {
        if (logo && logo.url) {
            return (
                <WordpressLink url={logo.url} key={index}>
                    <span className="imgtext">{logo.image.alt ? logo.image.alt : 'Security Logo'}</span>
                    <Visual extend={securityLogoStyle} image={logo.image} />
                </WordpressLink>
            );
        } else {
            return (
                <Visual
                    key={index}
                    extend={securityLogoStyle}
                    image={logo.image}
                />
            );
        }
    };

    return (
        <div style={{ background: colors.white, position: "relative" }}>
            <ReturnToTop href="#">Return to Top</ReturnToTop>
            <Container>
                <ContentMaxWidth extend={wrapperStyles}>
                    {/* Footer security logos, if any */}
                    {footerSecurityLogos && footerSecurityLogos.length && (
                        <SecurityLogos>
                            <WordpressLink url="/" extend={footerLogoStyle}>
                                <span className="imgtext">Return to Home</span>
                                <img src={ringLogo} alt="Revenue.io Logo" />
                            </WordpressLink>
                            {footerSecurityLogos.map((logo, index) => {
                                if (!logo) return null;
                                return renderSecurityImage(logo, index);
                            })}
                        </SecurityLogos>
                    )}

                    <FelaComponent as="nav" style={footerNav}>
                        {footerMenuData &&
                            footerMenuData.items &&
                            footerMenuData.items.map(item => {
                                return (
                                    <NavUl
                                        style={{ marginTop: -8 }}
                                        key={item.ID}
                                        as="ul"
                                    >
                                        <FelaComponent style={footerLi} as="li">
                                            {makeLink(item, footerHeaderLink)}

                                            {item.child_items &&
                                                makeChildLinks(
                                                    item.child_items
                                                )}
                                        </FelaComponent>
                                    </NavUl>
                                );
                            })}
                    </FelaComponent>
                </ContentMaxWidth>
            </Container>
        </div>
    );
}

const makeLink = (item, style) => {
    // Support empty menu item headings from Wordpress
    const isEmptyLink =
        !item.title ||
        item.title === String.fromCharCode(160) ||
        item.title === " " ||
        item.title.toLowerCase() === "menu item" ||
        item.title.toLowerCase() === "empty";
    if (
        (item && item.child_items && item.child_items.length) ||
        item.url === "#" ||
        isEmptyLink
    ) {
        return (
            <p style={{ ...style, marginBottom: "inherit" }}>
                {isEmptyLink ? "" : item.title}
            </p>
        );
    }
    return (
        <WordpressLink url={item.url} label={item.title} skipLabel={true}>
            <FelaComponent style={style}>{item.title}</FelaComponent>
        </WordpressLink>
    );
};

const makeChildLinks = children => {
    return (
        <NavUl isChild={true} style={{ marginTop: 10 }} as="ul">
            {children.map(item => {
                return (
                    <FelaComponent style={footerLi} as="li" key={item.ID}>
                        {makeLink(item, footerLink)}
                    </FelaComponent>
                );
            })}
        </NavUl>
    );
};

const ReturnToTop = createComponent(
    () => ({
        width: 48,
        height: 48,
        borderRadius: "50%",
        backgroundColor: "#7549EA",
        position: "absolute",
        right: 20,
        top: -20,
        textIndent: "-9999em",
        overflow: "hidden",
        backgroundImage: `url(
        "data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48Z' fill='%237549EA'/%3E%3Cpath d='M34 22.75L24.2121 12.9621C24.095 12.845 23.905 12.845 23.7879 12.9621L14 22.75' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M24 12.75L24 35.25' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A"
    )`,
        minWidthMobile: {
            display: "none"
        }
    }),
    "a",
    ["href"]
);

const Container = createComponent(() => ({
    color: colors.white,
    extend: [
        fluid("paddingH", gutters.footer),
        fluid("paddingV", gutters.vertical)
    ]
}));

const wrapperStyles = {
    background: colors.blue,
    display: "flex",
    justifyContent: "flex-start",
    maxWidth: layout.siteMaxWidth,
    padding: "calc(48 * 1px)",
    borderRadius: "30px",

    maxWidthTabletLandscape: {
        flexWrap: "wrap"
    },
    maxWidthMobile: {
        justifyContent: "center"
    }
};

const footerNav = {
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidthTablet: {
        "> ul": {
            width: "14%",
            paddingRight: 20
        }
    },
    maxWidthTablet: {
        marginRight: 0,
        width: "100%"
    },
    maxWidthTabletLandscape: {
        order: 2,
        width: "100%",

        extend: [fluid("margin-top", { max: 70, min: 50 })]
    },
    maxWidthMobile: {
        width: "100%",
        order: 1,
        display: "block",
        columnCount: 2
    }
};

const footerLogoStyle = {
    width: 53,
    height: 51,
    display: "inline-block",
    marginBottom: 60,
    maxWidthTabletLandscape: {
        marginBottom: 0,
        marginRight: 60,
    },
    maxWidthMobile: {
        marginRight: 0,
        marginBottom: 60
    }
};

const SecurityLogos = createComponent(() => ({
    width: "10%",
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    alignItems: "center",
    marginRight: 20,
    maxWidthTabletLandscape: {
        flexDirection: "inherit",
        justifyContent: "center",
        width: "100%",
        alignItems: "center"
    },
    maxWidthMobile: {
        flexDirection: "column",
        justifyContent: "center",
        flexWrap: "wrap",
        alignItems: "center"
    },
    "& span.imgtext": {
      display: "none"
    }
}));

const securityLogoStyle = {
    "& .vv-image": {
        width: "auto",
        height: 24,
        marginBottom: 25,
        maxWidthTabletLandscape: {
            marginBottom: 0,
            marginRight: 60,
        },
        maxWidthMobile: {
            marginBottom: 25,
            marginRight: 0
        }
    },
    maxWidthMobile: {
        marginH: 20
    }
};

const NavUl = createComponent(({ isChild = false }) => ({
    padding: 0,
    ":last-of-type": {
        marginRight: 0
    },
    // Tablet
    maxWidthTablet: {
        marginBottom: 15
    },

    // Mobile
    maxWidthMobile: {
        extend: [
            {
                condition: !isChild,
                style: {
                    width: "100%",
                    marginBottom: 50
                }
            }
        ]
    }
}));

const footerLi = {
    display: "block",
    width: "100%"
};

const activeLinkStyle = {
    willChange: "border",
    transition: "border 0.2s ease",
    borderBottom: "1px solid transparent",

    whenActive: {
        borderBottom: "1px solid white"
    }
};

const footerHeaderLink = {
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: "bold",
    color: colors.white,
    display: "inline-block",
    minHeight: "25px",

    ...activeLinkStyle
};

const footerLink = {
    fontSize: 14,
    fontWeight: "normal",
    marginBottom: 10,
    color: colors.white,
    display: "inline-block",

    ...activeLinkStyle
};
